import React, { useRef, useEffect, useState } from 'react';
import { Modal, Button, Space } from 'antd';
import { PrinterOutlined, DownloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { systemService, SystemSummary } from '../../services/systemService';

interface StockInItem {
  id: number;
  medicine_id: number;
  medicine_name: string;
  medicine_code: string;
  quantity: number;
  unit: string;
  unit_price: number;
  lot_number: string;
  expiry_date: string;
  manufacturing_date: string;
  supplier_id: number;
  supplier_name: string;
  total_amount: number;
  notes: string;
}

interface StockInVoucher {
  id: number;
  voucher_number: string;
  supplier_id: number;
  supplier_name: string;
  total_amount: number;
  status: 'pending' | 'completed' | 'cancelled';
  notes: string;
  created_at: string;
  completed_at?: string;
  created_by_name: string;
}

interface StockInPrintData {
  voucher: StockInVoucher;
  items: StockInItem[];
}

interface StockInPrintModalProps {
  visible: boolean;
  onCancel: () => void;
  data: StockInPrintData | null;
  isLoading?: boolean;
}

const StockInPrintModal: React.FC<StockInPrintModalProps> = ({
  visible,
  onCancel,
  data,
  isLoading = false
}) => {
  const printRef = useRef<HTMLDivElement>(null);
  const [systemInfo, setSystemInfo] = useState<SystemSummary | null>(null);
  const [isLoadingSystemInfo, setIsLoadingSystemInfo] = useState(false);

  useEffect(() => {
    if (visible && !systemInfo) {
      setIsLoadingSystemInfo(true);
      systemService.getSystemSummary()
        .then((info) => setSystemInfo(info))
        .catch(() => {})
        .finally(() => setIsLoadingSystemInfo(false));
    }
  }, [visible, systemInfo]);

  if (isLoading || isLoadingSystemInfo) {
    return (
      <Modal title="In phiếu nhập kho" open={visible} onCancel={onCancel} width={1000} footer={null} destroyOnClose>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <div>Đang tải dữ liệu phiếu và thông tin hệ thống...</div>
        </div>
      </Modal>
    );
  }

  if (!data || !data.voucher || !data.items) return null;

  const subtotal = data.items.reduce((sum, item) => sum + ((item.quantity || 0) * (item.unit_price || 0)), 0) || 0;

  // Hàm render nội dung phiếu (dùng cho cả xem và in)
  const renderVoucherContent = () => (
    <div style={{ fontFamily: 'Roboto, Arial, sans-serif', padding: '20px', backgroundColor: 'white', maxWidth: '800px', margin: '0 auto', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 32 }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 24, color: '#1a202c' }}>{systemInfo?.company?.name || 'CÔNG TY TNHH ABC'}</div>
          <div style={{ fontSize: 14, color: '#666' }}>Địa chỉ: {systemInfo?.company?.address || ''}</div>
          <div style={{ fontSize: 14, color: '#666' }}>Điện thoại: {systemInfo?.company?.phone || ''}</div>
          <div style={{ fontSize: 14, color: '#666' }}>Mã số thuế: {systemInfo?.company?.tax_code || ''}</div>
          {systemInfo?.company?.email && <div style={{ fontSize: 14, color: '#666' }}>Email: {systemInfo.company.email}</div>}
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontWeight: 700, fontSize: 32, color: '#005A9C', marginBottom: 8 }}>PHIẾU NHẬP KHO</div>
          <div style={{ fontSize: 16 }}>Số: {data.voucher.voucher_number}</div>
          <div style={{ fontSize: 16 }}>Ngày {dayjs(data.voucher.created_at).format('DD')} tháng {dayjs(data.voucher.created_at).format('MM')} năm {dayjs(data.voucher.created_at).format('YYYY')}</div>
        </div>
      </div>
      {/* Thông tin phiếu */}
      <div style={{ borderTop: '1px solid #e2e8f0', borderBottom: '1px solid #e2e8f0', padding: '16px 0', marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
          <div><b>Nhà cung cấp:</b> {data.voucher.supplier_name}</div>
          <div><b>Người lập phiếu:</b> {data.voucher.created_by_name}</div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
          <div><b>Trạng thái phiếu:</b> {data.voucher.status === 'completed' ? 'Đã hoàn thành' : data.voucher.status === 'cancelled' ? 'Đã hủy' : 'Chờ xử lý'}</div>
          <div><b>Ngày hoàn thành:</b> {data.voucher.completed_at ? dayjs(data.voucher.completed_at).format('DD/MM/YYYY HH:mm') : 'Chưa hoàn thành'}</div>
        </div>
      </div>
      {/* Bảng thuốc */}
      <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 24 }}>
        <thead>
          <tr style={{ background: '#f7fafc', color: '#4a5568' }}>
            <th style={{ border: '1px solid #e2e8f0', padding: 8 }}>STT</th>
            <th style={{ border: '1px solid #e2e8f0', padding: 8 }}>Tên thuốc</th>
            <th style={{ border: '1px solid #e2e8f0', padding: 8 }}>Mã số</th>
            <th style={{ border: '1px solid #e2e8f0', padding: 8 }}>Số lô</th>
            <th style={{ border: '1px solid #e2e8f0', padding: 8 }}>ĐVT</th>
            <th style={{ border: '1px solid #e2e8f0', padding: 8 }}>Số lượng</th>
            <th style={{ border: '1px solid #e2e8f0', padding: 8 }}>Đơn giá (VNĐ)</th>
            <th style={{ border: '1px solid #e2e8f0', padding: 8 }}>Thành tiền (VNĐ)</th>
          </tr>
        </thead>
        <tbody>
          {data.items.map((item, idx) => (
            <tr key={item.id}>
              <td style={{ border: '1px solid #e2e8f0', padding: 8, textAlign: 'center' }}>{idx + 1}</td>
              <td style={{ border: '1px solid #e2e8f0', padding: 8 }}>{item.medicine_name}</td>
              <td style={{ border: '1px solid #e2e8f0', padding: 8 }}>{item.medicine_code}</td>
              <td style={{ border: '1px solid #e2e8f0', padding: 8 }}>{item.lot_number}</td>
              <td style={{ border: '1px solid #e2e8f0', padding: 8 }}>{item.unit}</td>
              <td style={{ border: '1px solid #e2e8f0', padding: 8, textAlign: 'right' }}>{item.quantity}</td>
              <td style={{ border: '1px solid #e2e8f0', padding: 8, textAlign: 'right' }}>{(item.unit_price || 0).toLocaleString('vi-VN')}</td>
              <td style={{ border: '1px solid #e2e8f0', padding: 8, textAlign: 'right' }}>{((item.quantity || 0) * (item.unit_price || 0)).toLocaleString('vi-VN')}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {/* Tổng cộng */}
      <div style={{ textAlign: 'right', marginBottom: 24 }}>
        <span style={{ fontWeight: 700, fontSize: 16 }}>TỔNG CỘNG THANH TOÁN: </span>
        <span style={{ fontWeight: 700, fontSize: 20, color: '#005A9C' }}>{subtotal.toLocaleString('vi-VN')} VNĐ</span>
      </div>
      {/* Ghi chú */}
      {data.voucher.notes && (
        <div style={{ marginBottom: 24, background: '#f9f9f9', borderLeft: '4px solid #005A9C', borderRadius: 4, padding: 12 }}>
          <b>Ghi chú:</b> {data.voucher.notes}
        </div>
      )}
      {/* Chữ ký */}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 48 }}>
        <div style={{ textAlign: 'center', flex: 1 }}>
          <div style={{ fontWeight: 700, marginBottom: 32 }}>Người lập phiếu</div>
          <div style={{ fontStyle: 'italic', color: '#718096', marginTop: 48 }}>{data.voucher.created_by_name}</div>
        </div>
        <div style={{ textAlign: 'center', flex: 1 }}>
          <div style={{ fontWeight: 700, marginBottom: 32 }}>Người giao hàng</div>
          <div style={{ fontStyle: 'italic', color: '#718096', marginTop: 48 }}>(Ký, họ tên)</div>
        </div>
        <div style={{ textAlign: 'center', flex: 1 }}>
          <div style={{ fontWeight: 700, marginBottom: 32 }}>Người nhận</div>
          <div style={{ fontStyle: 'italic', color: '#718096', marginTop: 48 }}>(Ký, họ tên)</div>
        </div>
      </div>
    </div>
  );

  const handlePrint = () => {
    if (printRef.current) {
      const printWindow = window.open('', '_blank', 'width=800,height=600');
      if (printWindow) {
        printWindow.document.write('<html><head><title>Phiếu Nhập Kho</title></head><body>' + printRef.current.innerHTML + '</body></html>');
        printWindow.document.close();
        printWindow.focus();
        setTimeout(() => {
          printWindow.print();
          printWindow.close();
        }, 500);
      }
    }
  };

  return (
    <Modal
      title="In phiếu nhập kho"
      open={visible}
      onCancel={onCancel}
      width={1000}
      footer={
        <div style={{ textAlign: 'right' }}>
          <Space>
            <Button icon={<DownloadOutlined />} onClick={async () => {
              if (!data || !printRef.current) return;
              try {
                const canvas = await html2canvas(printRef.current, { scale: 2, useCORS: true, allowTaint: true, backgroundColor: '#ffffff' });
                const imgData = canvas.toDataURL('image/png');
                const pdf = new jsPDF('p', 'mm', 'a4');
                const imgWidth = 210; const pageHeight = 295; const imgHeight = (canvas.height * imgWidth) / canvas.width;
                let heightLeft = imgHeight; let position = 0;
                pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight); heightLeft -= pageHeight;
                while (heightLeft >= 0) { position = heightLeft - imgHeight; pdf.addPage(); pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight); heightLeft -= pageHeight; }
                pdf.save(`Phiếu nhập kho - ${data.voucher.voucher_number}.pdf`);
              } catch (error) { alert('Có lỗi khi tạo file PDF. Vui lòng thử lại.'); }
            }}>Tải xuống</Button>
            <Button type="primary" icon={<PrinterOutlined />} onClick={handlePrint}>In phiếu</Button>
          </Space>
        </div>
      }
      destroyOnClose
    >
      <div ref={printRef}>{renderVoucherContent()}</div>
    </Modal>
  );
};

export default StockInPrintModal; 